import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { faClose, faMinus, faPlus, faUser, faUtensils } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useRodizio } from "../../../hooks/Rodizio/useRodizio"
import type { ICartItem } from "../../../Interfaces/ICardItem"
import type { IProductRequest } from "../../../Interfaces/IProductRequest"
import { OrderDrink } from "../../../Objects/Order/OrderDrink"
import type { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { OrderMeal } from "../../../Objects/Order/OrderMeal"
import { ERodizioSessionStatus } from "../../../Objects/Rodizio/ERodizioSessionStatus"
import { type RodizioManagement } from "../../../Objects/Rodizio/RodizioManagement"
import { type RodizioSession } from "../../../Objects/Rodizio/RodizioSession"
import { AddMealRequestPupop } from "../../../PublicMenu/Pupop/AddMealRequestPupop"
import { buildAllProducts, mapAddtionals, mapSelections } from "../../../PublicMenu/Helpers/BuildProductList"
import { getRodizioGenericFoodId } from "../../../PublicMenu/Helpers/RodizioProductHelpers"
import { upsertCartItem } from "../../../PublicMenu/Helpers/CartItemHelpers"
import type { IAddtionalRequest } from "../../../Interfaces/IAddtionalRequest"
import type { ISelectedSelectionRequest } from "../../../Interfaces/ISelectionRequest"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import GetSpace from "../../../utils/space"

interface Props {
  order: OrderEntity
  onClose: () => void
  initialSession?: RodizioSession
}

export const RodizioOrderPopup: React.FC<Props> = ({ order, onClose, initialSession }) => {
  const { t } = useTranslation("rodizio")

  const api = useRodizio()

  const apiRef = useRef(api)

  const { setNewConfirmation } = useConfirmation()

  const [management, setManagement] = useState<RodizioManagement | null>(null)

  const [session, setSession] = useState<RodizioSession | null>(initialSession ?? null)

  const [planId, setPlanId] = useState(0)

  const [orderConcurrencyToken, setOrderConcurrencyToken] = useState(order.concurrencyToken)

  const [cart, setCart] = useState<ICartItem[]>([])

  const [editingProduct, setEditingProduct] = useState<IProductRequest | null>(null)

  const roundRequestIdRef = useRef(crypto.randomUUID())

  useEffect(() => {
    apiRef.current = api
  }, [api])

  const customer = order.customerEntity

  const hasValidCustomer = Boolean(customer && !customer.isDefaultCustomer)

  const customerName = order.getCustomerDisplayName()

  const load = useCallback(async () => {
    const [configuration, currentSessions] = await Promise.all([
      apiRef.current.getOperationConfiguration(),

      apiRef.current.getOrderSessions(order.id),
    ])

    if (configuration) {
      setManagement(configuration)

      setPlanId((current) => current || configuration.plans.find((plan) => plan.isActive)?.id || 0)
    }

    if (currentSessions) {
      setSession(currentSessions.find((current) => current.status === ERodizioSessionStatus.Active) ?? null)
    }
  }, [order.id])

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0)
    return () => window.clearTimeout(timer)
  }, [load])

  const products = useMemo(
    () =>
      session
        ? buildAllProducts()
            .filter((product) => {
              const id = getRodizioGenericFoodId(product)
              return Boolean(id && session.productIds.includes(id))
            })
            .map((product) => ({
              ...product,
              price: 0,
              oldPrice: undefined,
              badges: [...(product.badges ?? []), t("public.included")],
            }))
        : [],
    [session, t],
  )

  const addToCart = (
    product: IProductRequest,
    selectedAddOns: IAddtionalRequest[],
    selectedSelections: ISelectedSelectionRequest[],
    quantity: number,
    notes: string,
  ) => {
    roundRequestIdRef.current = crypto.randomUUID()
    setCart((current) =>
      upsertCartItem({
        currentCart: current,
        product,
        selectedAddOns,
        selectedSelections,
        quantity,
        notes,
        isReservation: false,
        reservationDate: "",
        reservationTime: "",
      }),
    )
    setEditingProduct(null)
  }

  const addProduct = (product: IProductRequest) => {
    const requiresConfiguration =
      Boolean(product.availableAddOns?.length) || Boolean(product.availableSelections?.length)

    if (requiresConfiguration) {
      setEditingProduct(product)

      return
    }

    roundRequestIdRef.current = crypto.randomUUID()

    setCart((current) =>
      upsertCartItem({
        currentCart: current,
        product,
        selectedAddOns: [],
        selectedSelections: [],
        quantity: 1,
        notes: "",
      }),
    )
  }

  const removeProduct = (productId: string) => {
    roundRequestIdRef.current = crypto.randomUUID()

    setCart((current) => {
      let itemIndex = -1
      for (let index = current.length - 1; index >= 0; index -= 1) {
        if (current[index]?.id === productId) {
          itemIndex = index

          break
        }
      }
      if (itemIndex < 0) return current

      return current.flatMap((item, index) => {
        if (index !== itemIndex) return [item]

        return item.quantity > 1 ? [{ ...item, quantity: item.quantity - 1 }] : []
      })
    })
  }

  const sendRound = async () => {
    if (!session || cart.length === 0) return

    const meals = cart
      .filter((item) => item.id.startsWith("meal-") || item.id.startsWith("combo-"))
      .flatMap((item) => {
        const mealId = item.id.startsWith("combo-") ? Number(item.linkedMealId) : Number(item.id.replace("meal-", ""))
        return Array.from(
          { length: item.quantity },
          () =>
            new OrderMeal({
              mealEntityId: mealId,
              description: item.notes ?? "",
              addtionals: mapAddtionals(item),
              selections: mapSelections(item),
            }),
        )
      })

    const drinks = cart
      .filter((item) => item.id.startsWith("drink-"))
      .flatMap((item) =>
        Array.from(
          { length: item.quantity },
          () =>
            new OrderDrink({
              drinkEntityId: Number(item.id.replace("drink-", "")),
              description: item.notes ?? "",
              addtionals: mapAddtionals(item),
              selections: mapSelections(item),
            }),
        ),
      )

    const updated = await api.addWaiterItems(session.id, {
      requestId: roundRequestIdRef.current,
      concurrencyToken: session.concurrencyToken,
      meals,
      drinks,
    })
    if (!updated) return
    onClose()
  }

  const releaseRodizio = async (nextPlanId: number) => {
    const created = await api.startSession({
      orderId: order.id,
      planId: nextPlanId,
      concurrencyToken: orderConcurrencyToken,
    })

    if (!created) return

    setPlanId(nextPlanId)

    setOrderConcurrencyToken(created.concurrencyToken)

    setSession(created)
  }

  const handleReleaseClick = () => {
    const plan = activePlans.find((current) => current.id === planId)

    if (!plan || !hasValidCustomer) return

    setNewConfirmation(
      t("waiter.confirm_release_title"),
      t("waiter.confirm_release_message", {
        name: customerName,
        plan: plan.name,
        price: plan.price.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
      }),
      () => {
        void releaseRodizio(plan.id)
      },
    )
  }

  const finishOrRemoveRodizio = async (isCancellation: boolean) => {
    if (!session) return

    const finished = await api.finishSession(session.id, session.concurrencyToken, isCancellation)

    if (!finished) return

    if (isCancellation) {
      await load()

      return
    }
    onClose()
  }

  const handleFinishOrRemoveClick = () => {
    if (!session) return

    const isRemoval = !session.hasRounds

    setNewConfirmation(
      isRemoval ? t("waiter.confirm_remove_title") : t("waiter.confirm_finish_title"),
      isRemoval
        ? t("waiter.confirm_remove_message", { name: session.participantName })
        : t("waiter.confirm_finish_message", { name: session.participantName }),
      () => {
        void finishOrRemoveRodizio(isRemoval)
      },
    )
  }

  const activePlans = management?.plans.filter((plan) => plan.isActive) ?? []

  const selectedItemsCount = cart.reduce((total, item) => total + item.quantity, 0)

  const isRoundLimitExceeded = Boolean(management && selectedItemsCount > management.settings.maximumItemsPerRequest)

  const isRoundLimitReached = Boolean(management && selectedItemsCount >= management.settings.maximumItemsPerRequest)

  const getProductQuantity = (productId: string) =>
    cart.reduce((total, item) => total + (item.id === productId ? item.quantity : 0), 0)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("waiter.title")}
              icon={faUtensils}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex-nowrap`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="rodizio.waiter"
                    title={t("helpPopup:global.rodizioWaiter.title")}
                    description={t("helpPopup:global.rodizioWaiter.description")}
                    steps={t("helpPopup:global.rodizioWaiter.steps", { returnObjects: true }) as string[]}
                    bullets={t("helpPopup:global.rodizioWaiter.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace left={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    descriptionKey="interactionDescriptions:rodizio.waiter.close"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="grid max-h-[78vh] gap-4 overflow-y-auto p-2">
                {!management?.settings.isEnabled ? (
                  <p className="rounded-lg bg-amber-50 p-4 font-bold text-amber-800">{t("waiter.disabled")}</p>
                ) : (
                  <>
                    <section className="rounded-lg border-2 border-slate-200 p-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700">
                          <FontAwesomeIcon icon={faUser} />
                        </span>

                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-black uppercase tracking-wide text-orange-700">
                            {t("waiter.order_customer")}
                          </p>

                          <p className="truncate font-black text-slate-900">
                            {hasValidCustomer ? customerName : t("waiter.no_customer")}
                          </p>
                        </div>

                        {session?.status === ERodizioSessionStatus.Active && session.accessCode && (
                          <div
                            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-2 py-1 text-blue-900"
                            title={t("waiter.access_code_help")}
                          >
                            <span className="text-[9px] font-bold uppercase leading-tight tracking-wide">
                              {t("waiter.access_code")}
                            </span>

                            <span className="font-mono text-sm font-black tracking-[0.15em]">{session.accessCode}</span>
                          </div>
                        )}
                      </div>

                      {!hasValidCustomer && (
                        <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm font-bold text-amber-800">
                          {t("waiter.no_customer_warning")}
                        </p>
                      )}
                    </section>

                    {!session && hasValidCustomer && (
                      <section className="rounded-lg border-2 border-slate-200 p-3">
                        <h3 className="font-black text-slate-900">{t("waiter.release_title")}</h3>

                        {activePlans.length === 0 ? (
                          <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm font-bold text-amber-800">
                            {t("waiter.no_active_plans")}
                          </p>
                        ) : (
                          <div className="mt-3 grid gap-3">
                            {activePlans.length > 1 && (
                              <GenericDropDown
                                legend={t("waiter.plan")}
                                data={activePlans.map((plan) => ({
                                  label: `${plan.name} — ${plan.price.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`,
                                  value: String(plan.id),
                                }))}
                                initialState={String(planId)}
                                handlerFuncion={(event) => setPlanId(Number(event.target.value))}
                                descriptionKey="interactionDescriptions:rodizio.waiter.plan"
                              />
                            )}

                            <Button
                              label={api.isLoading ? t("common.saving") : t("waiter.release")}
                              disabled={api.isLoading || planId <= 0}
                              onClick={handleReleaseClick}
                              bgColor={THEME.colors.buttonBGSaveColor}
                              descriptionKey="interactionDescriptions:rodizio.waiter.release"
                            />
                          </div>
                        )}
                      </section>
                    )}

                    {session?.status === ERodizioSessionStatus.Active && (
                      <section className="rounded-lg border-2 border-orange-200 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="font-black text-slate-900">
                            {t("waiter.round_for", { name: session.participantName })}
                          </h3>

                          <button
                            type="button"
                            disabled={api.isLoading}
                            onClick={handleFinishOrRemoveClick}
                            className="rounded-lg border-2 border-red-600 bg-red-600 px-4 py-2 text-xs font-black uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-red-700 active:scale-95 disabled:opacity-50"
                            data-interaction-description-key="interactionDescriptions:rodizio.waiter.finishOrRemove"
                          >
                            {session.hasRounds ? t("waiter.finish") : t("waiter.remove")}
                          </button>
                        </div>
                        {!management.settings.allowWaiterOrders ? (
                          <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm font-bold text-amber-800">
                            {t("waiter.rounds_disabled")}
                          </p>
                        ) : (
                          <div className="mt-3 grid max-h-[46vh] gap-2 overflow-y-auto pr-1">
                            {products.map((product) => {
                              const quantity = getProductQuantity(product.id)

                              return (
                                <article
                                  key={product.id}
                                  className={`flex items-center gap-3 rounded-lg border-2 p-2 shadow-[0_2px_8px_rgba(0,0,0,0.03)] transition-colors ${quantity > 0 ? "border-orange-400 bg-orange-50" : "border-slate-200 bg-white"}`}
                                >
                                  <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                                    {product.image ? (
                                      <img src={product.image} alt="" className="h-full w-full object-cover" />
                                    ) : (
                                      <span className="text-xl">{product.categoryIcon ?? "🍽️"}</span>
                                    )}
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-black leading-tight text-slate-900">
                                      {product.name}
                                    </p>

                                    <p className="text-[11px] font-black uppercase tracking-wide text-emerald-600">
                                      {t("public.included")}
                                    </p>
                                  </div>

                                  <div className="flex shrink-0 items-center gap-1">
                                    <button
                                      type="button"
                                      disabled={api.isLoading || quantity === 0}
                                      onClick={() => removeProduct(product.id)}
                                      className="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-slate-300 bg-white text-slate-700 active:scale-95 disabled:opacity-35"
                                      aria-label={t("waiter.decrease_item", { product: product.name })}
                                      data-interaction-description-key="interactionDescriptions:rodizio.waiter.decreaseItem"
                                    >
                                      <FontAwesomeIcon icon={faMinus} />
                                    </button>

                                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-base font-black tabular-nums text-slate-900">
                                      {quantity}
                                    </span>

                                    <button
                                      type="button"
                                      disabled={api.isLoading || isRoundLimitReached}
                                      onClick={() => addProduct(product)}
                                      className="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-orange-400 bg-orange-500 text-white active:scale-95 disabled:opacity-35"
                                      aria-label={t("waiter.increase_item", { product: product.name })}
                                      data-interaction-description-key="interactionDescriptions:rodizio.waiter.increaseItem"
                                    >
                                      <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                  </div>
                                </article>
                              )
                            })}
                          </div>
                        )}

                        {management.settings.allowWaiterOrders && cart.length > 0 && (
                          <div className="mt-3 rounded-lg bg-slate-50 p-3">
                            <p className="font-bold text-slate-700">
                              {t("waiter.selected_items", { count: selectedItemsCount })}
                            </p>

                            {isRoundLimitExceeded && (
                              <p className="mt-2 rounded-lg bg-amber-50 p-2 text-sm font-bold text-amber-800">
                                {t("public.round_limit", { count: management?.settings.maximumItemsPerRequest })}
                              </p>
                            )}

                            <Button
                              label={api.isLoading ? t("public.sending") : t("public.send_round")}
                              disabled={api.isLoading || isRoundLimitExceeded}
                              onClick={sendRound}
                              bgColor={THEME.colors.buttonBGSaveColor}
                              descriptionKey="interactionDescriptions:rodizio.waiter.send"
                            />
                          </div>
                        )}
                      </section>
                    )}
                  </>
                )}
              </div>
            </Animate>
          </section>
        </Animate>
      </div>

      {editingProduct && (
        <AddMealRequestPupop
          product={editingProduct}
          isRodizioIncluded
          isRodizioAdditionalsIncluded={Boolean(session?.includeAdditionals)}
          onClose={() => setEditingProduct(null)}
          onConfirm={(product, additionals, selections, quantity, notes) =>
            addToCart(product, additionals, selections, quantity, notes)
          }
        />
      )}
    </ModalPortal>
  )
}
